// @ts-nocheck
import * as Utils from '../../primitives/utils.js';
import Random from '../../primitives/Random.js';
import P2PKH from '../../script/templates/P2PKH.js';
import PublicKey from '../../primitives/PublicKey.js';
import { createNonce } from '../utils/createNonce.js';
import { Peer } from '../Peer.js';
import { SimplifiedFetchTransport } from '../transports/SimplifiedFetchTransport.js';
import { SessionManager } from '../SessionManager.js';
import { getVerifiableCertificates } from '../utils/index.js';
const PAYMENT_VERSION = '1.0';
const AUTH_RESPONSE_TIMEOUT_MS = 30000;
const MAX_PENDING_AUTH_REQUESTS = 1000;
/**
 * AuthFetch provides a lightweight fetch client for interacting with servers
 * over a simplified HTTP transport mechanism. It integrates session management, peer communication,
 * and certificate handling to enable secure and mutually-authenticated requests.
 *
 * Additionally, it automatically handles 402 Payment Required responses by creating
 * and sending BSV payment transactions when necessary.
 */
export class AuthFetch {
    sessionManager;
    wallet;
    pendingRequestNonces = new Set();
    certificatesReceived = [];
    requestedCertificates;
    originator;
    peers = {};
    /**
     * Constructs a new AuthFetch instance.
     * @param wallet - The wallet instance for signing and authentication.
     * @param requestedCertificates - Optional set of certificates to request from peers.
     */
    constructor(wallet, requestedCertificates, sessionManager, originator) {
        this.wallet = wallet;
        this.requestedCertificates = requestedCertificates;
        // See `Peer.sessionManager`: field stays typed as the synchronous
        // `SessionManager` for back-compat; if an `AsyncSessionManager` is
        // injected, the underlying Peer awaits all calls internally.
        this.sessionManager = (sessionManager ?? new SessionManager());
        this.originator = originator;
    }
    /**
     * Mutually authenticates and sends a HTTP request to a server.
     *
     * 1) Attempt the request.
     * 2) If 402 Payment Required, automatically create and send payment.
     * 3) Return the final response.
     *
     * @param url - The URL to send the request to.
     * @param config - Configuration options for the request, including method, headers, body,
     *   optional payment retry controls, and optional `labels` merged onto any BRC-105 payment action.
     * @returns A promise that resolves with the server's response, structured as a Response-like object.
     *
     * @throws Will throw an error if unsupported headers are used or other validation fails.
     */
    async fetch(url, config = {}) {
        if (typeof config.retryCounter === 'number') {
            if (config.retryCounter <= 0) {
                throw new Error('Request failed after maximum number of retries.');
            }
            config.retryCounter--;
        }
        const response = await new Promise((resolve, reject) => {
            void (async () => {
                try {
                    // Apply defaults
                    const { method = 'GET', headers = {}, body } = config;
                    // Extract a base url
                    const parsedUrl = new URL(url);
                    const baseURL = parsedUrl.origin;
                    const peerToUse = await this.getOrCreatePeer(baseURL);
                    if (peerToUse.supportsMutualAuth === false) {
                        resolve(await this.handleFetchAndValidate(url, config, peerToUse));
                        return;
                    }
                    // Serialize the simplified fetch request.
                    const requestNonce = Random(32);
                    const requestNonceAsBase64 = Utils.toBase64(requestNonce);
                    const writer = await this.serializeRequest(method, headers, body, parsedUrl, requestNonce);
                    // Setup general message listener to resolve requests once a response is received
                    if (this.pendingRequestNonces.size >= MAX_PENDING_AUTH_REQUESTS) {
                        throw new Error('Authentication request capacity exceeded.');
                    }
                    let listenerId;
                    let responseTimeout;
                    let cleaned = false;
                    const cleanup = () => {
                        if (cleaned)
                            return;
                        cleaned = true;
                        if (listenerId !== undefined &&
                            typeof peerToUse.peer.stopListeningForGeneralMessages === 'function') {
                            peerToUse.peer.stopListeningForGeneralMessages(listenerId);
                        }
                        clearTimeout(responseTimeout);
                        this.pendingRequestNonces.delete(requestNonceAsBase64);
                    };
                    const resolveRequest = (response) => {
                        cleanup();
                        resolve(response);
                    };
                    const rejectRequest = (error) => {
                        cleanup();
                        reject(error);
                    };
                    this.pendingRequestNonces.add(requestNonceAsBase64);
                    listenerId = peerToUse.peer.listenForGeneralMessages((senderPublicKey, payload) => {
                        const responseValue = this.parseAuthenticatedResponse(baseURL, requestNonceAsBase64, senderPublicKey, payload);
                        if (responseValue !== undefined)
                            resolveRequest(responseValue);
                    });
                    responseTimeout = setTimeout(() => {
                        rejectRequest(new Error('Timed out waiting for authenticated response.'));
                    }, AUTH_RESPONSE_TIMEOUT_MS);
                    // Before sending general messages to the peer, ensure that no certificate requests are pending.
                    // This way, the user would need to choose to either allow or reject the certificate request first.
                    // If the server has a resource that requires certificates to be sent before access would be granted,
                    // this makes sure the user has a chance to send the certificates before the resource is requested.
                    try {
                        if (peerToUse.pendingCertificateRequests.length > 0) {
                            await this.waitForPendingCertificateRequests(peerToUse);
                        }
                        // Send the request, now that all listeners are set up
                        await peerToUse.peer.toPeer(writer.toArray(), peerToUse.identityKey);
                    }
                    catch (error) {
                        cleanup();
                        try {
                            resolveRequest(await this.recoverAuthenticatedSend(error, baseURL, url, config, peerToUse));
                        }
                        catch (recoveryError) {
                            rejectRequest(recoveryError);
                        }
                    }
                }
                catch (error) {
                    reject(error);
                }
            })();
        });
        // Check if server requires payment to access the requested route
        if (response.status === 402) {
            // Create and attach a payment, then retry
            return await this.handlePaymentAndRetry(url, config, response);
        }
        return response;
    }
    async getOrCreatePeer(baseURL) {
        const existingPeer = this.peers[baseURL];
        if (existingPeer !== undefined)
            return existingPeer;
        const newPeer = new Peer(this.wallet, new SimplifiedFetchTransport(baseURL), this.requestedCertificates, this.sessionManager, undefined, this.originator);
        await newPeer.ready;
        const peerState = {
            peer: newPeer,
            pendingCertificateRequests: []
        };
        this.peers[baseURL] = peerState;
        newPeer.listenForCertificatesReceived((_senderPublicKey, certs) => {
            this.certificatesReceived.push(...certs);
        });
        newPeer.listenForCertificatesRequested((async (verifier, requestedCertificates) => {
            try {
                peerState.pendingCertificateRequests.push(true);
                const certificatesToInclude = await getVerifiableCertificates(this.wallet, requestedCertificates, verifier, this.originator);
                if (certificatesToInclude.length > 0) {
                    await newPeer.sendCertificateResponse(verifier, certificatesToInclude);
                }
            }
            finally {
                // Give the backend 500 ms to process the certificates we just sent, before releasing the queue entry.
                await this.wait(500);
                peerState.pendingCertificateRequests.shift();
            }
        }));
        return peerState;
    }
    isStaleSessionError(error, peerToUse) {
        return (error instanceof Error &&
            (error.message.includes('Session not found for nonce') ||
                (error.message.includes('without valid BSV authentication') &&
                    peerToUse.identityKey != null &&
                    error.details?.status === 401)));
    }
    async recoverAuthenticatedSend(error, baseURL, url, config, peerToUse) {
        if (this.isStaleSessionError(error, peerToUse)) {
            delete this.peers[baseURL];
            config.retryCounter ??= 3;
            return await this.fetch(url, config);
        }
        if (error instanceof Error && error.message.includes('HTTP server failed to authenticate')) {
            return await this.handleFetchAndValidate(url, config, peerToUse);
        }
        throw error;
    }
    parseAuthenticatedResponse(baseURL, requestNonceAsBase64, senderPublicKey, payload) {
        const responseReader = new Utils.Reader(payload);
        const responseNonceAsBase64 = Utils.toBase64(responseReader.read(32));
        if (responseNonceAsBase64 !== requestNonceAsBase64)
            return undefined;
        const peerState = this.peers[baseURL];
        if (peerState !== undefined) {
            peerState.identityKey = senderPublicKey;
            peerState.supportsMutualAuth = true;
        }
        const statusCode = responseReader.readVarIntNum();
        const responseHeaders = {};
        const nHeaders = responseReader.readVarIntNum();
        for (let i = 0; i < nHeaders; i++) {
            const nHeaderKeyBytes = responseReader.readVarIntNum();
            const headerKey = Utils.toUTF8(responseReader.read(nHeaderKeyBytes));
            const nHeaderValueBytes = responseReader.readVarIntNum();
            responseHeaders[headerKey] = Utils.toUTF8(responseReader.read(nHeaderValueBytes));
        }
        responseHeaders['x-bsv-auth-identity-key'] = senderPublicKey;
        const responseBodyBytes = responseReader.readVarIntNum();
        const responseBody = responseBodyBytes > 0 ? new Uint8Array(responseReader.read(responseBodyBytes)) : null;
        return new Response(responseBody, {
            status: statusCode,
            statusText: `${statusCode}`,
            headers: new Headers(responseHeaders)
        });
    }
    /**
     * Request Certificates from a Peer
     * @param baseUrl
     * @param certificatesToRequest
     */
    async sendCertificateRequest(baseUrl, certificatesToRequest) {
        const parsedUrl = new URL(baseUrl);
        const baseURL = parsedUrl.origin;
        let peerToUse;
        if (this.peers[baseURL] === undefined) {
            const newTransport = new SimplifiedFetchTransport(baseURL);
            const newPeer = new Peer(this.wallet, newTransport, this.requestedCertificates, this.sessionManager, this.originator);
            await newPeer.ready;
            peerToUse = { peer: newPeer };
            this.peers[baseURL] = peerToUse;
        }
        else {
            peerToUse = { peer: this.peers[baseURL].peer };
        }
        // Return a promise that resolves when certificates are received
        const CERTIFICATE_REQUEST_TIMEOUT_MS = 30000;
        return await new Promise((resolve, reject) => {
            let settled = false;
            const cleanup = () => {
                settled = true;
                clearTimeout(timer);
                peerToUse.peer.stopListeningForCertificatesReceived(callbackId);
            };
            // Set up the listener before making the request
            const callbackId = peerToUse.peer.listenForCertificatesReceived((_senderPublicKey, certs) => {
                if (settled)
                    return;
                cleanup();
                this.certificatesReceived.push(...certs);
                resolve(certs);
            });
            const timer = setTimeout(() => {
                if (settled)
                    return;
                cleanup();
                reject(new Error(`sendCertificateRequest timed out after ${CERTIFICATE_REQUEST_TIMEOUT_MS}ms waiting for certificate response from ${baseURL}`));
            }, CERTIFICATE_REQUEST_TIMEOUT_MS);
            void peerToUse.peer
                .requestCertificates(certificatesToRequest, peerToUse.identityKey)
                .catch(error => {
                if (!settled) {
                    cleanup();
                    reject(error);
                }
            });
        });
    }
    /**
     * Return any certificates we've collected thus far, then clear them out.
     */
    consumeReceivedCertificates() {
        return this.certificatesReceived.splice(0);
    }
    writeOptionalText(writer, value) {
        if (value.length === 0) {
            writer.writeVarIntNum(-1);
            return;
        }
        const bytes = Utils.toArray(value);
        writer.writeVarIntNum(bytes.length);
        writer.write(bytes);
    }
    includedRequestHeaders(headers) {
        const includedHeaders = [];
        for (const [key, originalValue] of Object.entries(headers)) {
            const normalizedKey = key.toLowerCase();
            let value = originalValue;
            if (normalizedKey.startsWith('x-bsv-') || normalizedKey === 'authorization') {
                if (normalizedKey.startsWith('x-bsv-auth')) {
                    throw new Error('No BSV auth headers allowed here!');
                }
            }
            else if (normalizedKey.startsWith('content-type')) {
                value = value.split(';')[0].trim();
            }
            else {
                throw new Error('Unsupported header in the simplified fetch implementation. Only content-type, authorization, and x-bsv-* headers are supported.');
            }
            includedHeaders.push([normalizedKey, value]);
        }
        return includedHeaders.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
    }
    writeRequestHeaders(writer, headers) {
        writer.writeVarIntNum(headers.length);
        for (const [key, value] of headers) {
            const keyBytes = Utils.toArray(key, 'utf8');
            const valueBytes = Utils.toArray(value, 'utf8');
            writer.writeVarIntNum(keyBytes.length);
            writer.write(keyBytes);
            writer.writeVarIntNum(valueBytes.length);
            writer.write(valueBytes);
        }
    }
    defaultRequestBody(method, body, headers) {
        const methodsWithBody = ['POST', 'PUT', 'PATCH', 'DELETE'];
        if (!methodsWithBody.includes(method.toUpperCase()) || body !== undefined)
            return body;
        const contentType = headers.find(([key]) => key === 'content-type')?.[1];
        return contentType?.includes('application/json') === true ? '{}' : '';
    }
    async writeRequestBody(writer, body) {
        if (!body) {
            writer.writeVarIntNum(-1);
            return;
        }
        const bytes = await this.normalizeBodyToNumberArray(body);
        writer.writeVarIntNum(bytes.length);
        writer.write(bytes);
    }
    /**
     * Serializes the HTTP request to be sent over the Transport.
     *
     * @param method - The HTTP method (e.g., 'GET', 'POST') for the request.
     * @param headers - A record of HTTP headers to include in the request.
     * @param body - The body of the request, if applicable (e.g., for POST/PUT requests).
     * @param parsedUrl - The parsed URL object containing the full request URL.
     * @param requestNonce - A unique random nonce to ensure request integrity.
     * @returns A promise that resolves to a `Writer` containing the serialized request.
     *
     * @throws Will throw an error if unsupported headers are used or serialization fails.
     */
    async serializeRequest(method, headers, body, parsedUrl, requestNonce) {
        const writer = new Utils.Writer();
        // Write request nonce
        writer.write(requestNonce);
        // Method length
        writer.writeVarIntNum(method.length);
        // Method
        writer.write(Utils.toArray(method));
        this.writeOptionalText(writer, parsedUrl.pathname);
        this.writeOptionalText(writer, parsedUrl.search);
        // Construct headers to send / sign:
        // Ensures clients only provided supported HTTP request headers
        // - Include custom headers prefixed with x-bsv (excluding those starting with x-bsv-auth)
        // - Include a normalized version of the content-type header
        // - Include the authorization header
        const includedHeaders = this.includedRequestHeaders(headers);
        this.writeRequestHeaders(writer, includedHeaders);
        // If method typically carries a body and body is undefined, default it
        // This prevents signature verification errors due to mismatch default body types with express
        body = this.defaultRequestBody(method, body, includedHeaders);
        await this.writeRequestBody(writer, body);
        return writer;
    }
    /**
     * Handles a non-authenticated fetch requests and validates that the server is not claiming to be authenticated.
     */
    async handleFetchAndValidate(url, config, peerToUse) {
        const response = await fetch(url, config);
        response.headers.forEach(header => {
            if (header.toLocaleLowerCase().startsWith('x-bsv')) {
                throw new Error('The server is trying to claim it has been authenticated when it has not!');
            }
        });
        if (response.ok) {
            peerToUse.supportsMutualAuth = false;
            return response;
        }
        else {
            throw new Error(`Request failed with status: ${response.status}`);
        }
    }
    /**
     * If we get 402 Payment Required, we build a transaction via wallet.createAction()
     * and re-attempt the request with an x-bsv-payment header.
     */
    async handlePaymentAndRetry(url, config, originalResponse) {
        const paymentVersion = originalResponse.headers.get('x-bsv-payment-version');
        if (!paymentVersion || paymentVersion !== PAYMENT_VERSION) {
            throw new Error(`Unsupported x-bsv-payment-version response header. Client version: ${PAYMENT_VERSION}, Server version: ${paymentVersion}`);
        }
        const satoshisRequiredHeader = originalResponse.headers.get('x-bsv-payment-satoshis-required');
        if (!satoshisRequiredHeader) {
            throw new Error('Missing x-bsv-payment-satoshis-required response header.');
        }
        const satoshisRequired = Number.parseInt(satoshisRequiredHeader);
        if (Number.isNaN(satoshisRequired) || satoshisRequired <= 0) {
            throw new Error('Invalid x-bsv-payment-satoshis-required response header value.');
        }
        const serverIdentityKey = originalResponse.headers.get('x-bsv-auth-identity-key');
        if (typeof serverIdentityKey !== 'string') {
            throw new TypeError('Missing x-bsv-auth-identity-key response header.');
        }
        const derivationPrefix = originalResponse.headers.get('x-bsv-payment-derivation-prefix');
        if (typeof derivationPrefix !== 'string' || derivationPrefix.length < 1) {
            throw new Error('Missing x-bsv-payment-derivation-prefix response header.');
        }
        let paymentContext = config.paymentContext;
        if (paymentContext == null) {
            paymentContext = await this.createPaymentContext(url, config, satoshisRequired, serverIdentityKey, derivationPrefix);
        }
        else {
            const requirementsChanged = !this.isPaymentContextCompatible(paymentContext, satoshisRequired, serverIdentityKey, derivationPrefix);
            if (requirementsChanged) {
                this.logPaymentAttempt('warn', 'Server adjusted payment requirements; regenerating transaction', this.composePaymentLogDetails(url, paymentContext));
                paymentContext = await this.createPaymentContext(url, config, satoshisRequired, serverIdentityKey, derivationPrefix);
            }
        }
        if (paymentContext.attempts >= paymentContext.maxAttempts) {
            throw this.buildPaymentFailureError(url, paymentContext, new Error('Maximum payment attempts exceeded before retrying'));
        }
        const headersWithPayment = {
            ...config.headers
        };
        headersWithPayment['x-bsv-payment'] = JSON.stringify({
            derivationPrefix: paymentContext.derivationPrefix,
            derivationSuffix: paymentContext.derivationSuffix,
            transaction: paymentContext.transactionBase64
        });
        const nextConfig = {
            ...config,
            headers: headersWithPayment,
            paymentContext
        };
        if (typeof nextConfig.retryCounter !== 'number') {
            nextConfig.retryCounter = 3;
        }
        const attemptNumber = paymentContext.attempts + 1;
        const maxAttempts = paymentContext.maxAttempts;
        paymentContext.attempts = attemptNumber;
        const attemptDetails = this.composePaymentLogDetails(url, paymentContext);
        this.logPaymentAttempt('warn', `Attempting paid request (${attemptNumber}/${maxAttempts})`, attemptDetails);
        try {
            const response = await this.fetch(url, nextConfig);
            this.logPaymentAttempt('info', `Paid request attempt ${attemptNumber} succeeded`, attemptDetails);
            return response;
        }
        catch (error) {
            const errorEntry = this.createPaymentErrorEntry(paymentContext.attempts, error);
            paymentContext.errors.push(errorEntry);
            this.logPaymentAttempt('error', `Paid request attempt ${attemptNumber} failed`, {
                ...attemptDetails,
                error: {
                    message: errorEntry.message,
                    stack: errorEntry.stack
                }
            });
            if (paymentContext.attempts >= paymentContext.maxAttempts) {
                throw this.buildPaymentFailureError(url, paymentContext, error);
            }
            const delayMs = this.getPaymentRetryDelay(paymentContext.attempts);
            await this.wait(delayMs);
            return this.handlePaymentAndRetry(url, nextConfig, originalResponse);
        }
    }
    isPaymentContextCompatible(context, satoshisRequired, serverIdentityKey, derivationPrefix) {
        return (context.satoshisRequired === satoshisRequired &&
            context.serverIdentityKey === serverIdentityKey &&
            context.derivationPrefix === derivationPrefix);
    }
    async createPaymentContext(url, config, satoshisRequired, serverIdentityKey, derivationPrefix) {
        const derivationSuffix = await createNonce(this.wallet, undefined, this.originator);
        const { publicKey: derivedPublicKey } = await this.wallet.getPublicKey({
            protocolID: [2, '3241645161d8'],
            keyID: `${derivationPrefix} ${derivationSuffix}`,
            counterparty: serverIdentityKey
        }, this.originator);
        const lockingScript = new P2PKH()
            .lock(PublicKey.fromString(derivedPublicKey).toAddress())
            .toHex();
        const { tx } = await this.wallet.createAction({
            description: `Payment for request to ${new URL(url).origin}`,
            labels: this.buildPaymentActionLabels(config, derivationPrefix, derivationSuffix),
            outputs: [
                {
                    satoshis: satoshisRequired,
                    lockingScript,
                    customInstructions: JSON.stringify({
                        derivationPrefix,
                        derivationSuffix,
                        payee: serverIdentityKey
                    }),
                    outputDescription: 'HTTP request payment'
                }
            ],
            options: {
                randomizeOutputs: false
            }
        }, this.originator);
        const { publicKey: clientIdentityKey } = await this.wallet.getPublicKey({ identityKey: true }, this.originator);
        return {
            satoshisRequired,
            transactionBase64: Utils.toBase64(tx),
            derivationPrefix,
            derivationSuffix,
            serverIdentityKey,
            clientIdentityKey,
            attempts: 0,
            maxAttempts: this.getMaxPaymentAttempts(config),
            errors: [],
            requestSummary: this.buildPaymentRequestSummary(url, config)
        };
    }
    /**
     * Builds wallet action labels for a BRC-105 payment.
     * Always includes `brc105 <hexPrefix> <hexSuffix>` (base64 nonces hex-encoded
     * so label lowercasing is lossless); appends caller labels when provided.
     * Caller labels are passed through unchanged — wallet validateLabel trims/lowercases.
     */
    buildPaymentActionLabels(config, derivationPrefix, derivationSuffix) {
        const callerLabels = Array.isArray(config.labels) ? config.labels : [];
        return [
            `brc105 ${this.base64NonceToLabelHex(derivationPrefix)} ${this.base64NonceToLabelHex(derivationSuffix)}`,
            ...callerLabels
        ];
    }
    /** Hex-encode a base64 BRC-105 nonce for case-stable wallet labels. */
    base64NonceToLabelHex(base64Nonce) {
        return Utils.toHex(Utils.toArray(base64Nonce, 'base64'));
    }
    getMaxPaymentAttempts(config) {
        const attempts = typeof config.paymentRetryAttempts === 'number' ? config.paymentRetryAttempts : undefined;
        if (typeof attempts === 'number' && attempts > 0) {
            return Math.floor(attempts);
        }
        return 3;
    }
    buildPaymentRequestSummary(url, config) {
        const headers = { ...config.headers };
        const method = typeof config.method === 'string' ? config.method.toUpperCase() : 'GET';
        const bodySummary = this.describeRequestBodyForLogging(config.body);
        return {
            url,
            method,
            headers,
            bodyType: bodySummary.type,
            bodyByteLength: bodySummary.byteLength
        };
    }
    describeRequestBodyForLogging(body) {
        return (this.describeSimpleRequestBody(body) ??
            this.describePlatformRequestBody(body) ??
            this.describeSerializableRequestBody(body));
    }
    describeSimpleRequestBody(body) {
        if (body == null) {
            return { type: 'none', byteLength: 0 };
        }
        if (typeof body === 'string') {
            return { type: 'string', byteLength: Utils.toArray(body, 'utf8').length };
        }
        if (Array.isArray(body)) {
            if (body.every(item => typeof item === 'number')) {
                return { type: 'number[]', byteLength: body.length };
            }
            return { type: 'array', byteLength: body.length };
        }
        return undefined;
    }
    describePlatformRequestBody(body) {
        if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
            return { type: 'ArrayBuffer', byteLength: body.byteLength };
        }
        if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(body)) {
            return {
                type: body.constructor == null ? 'TypedArray' : body.constructor.name,
                byteLength: body.byteLength
            };
        }
        if (typeof Blob !== 'undefined' && body instanceof Blob) {
            return { type: 'Blob', byteLength: body.size };
        }
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
            return { type: 'FormData', byteLength: 0 };
        }
        if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
            const serialized = body.toString();
            return { type: 'URLSearchParams', byteLength: Utils.toArray(serialized, 'utf8').length };
        }
        if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) {
            return { type: 'ReadableStream', byteLength: 0 };
        }
        return undefined;
    }
    describeSerializableRequestBody(body) {
        try {
            const serialized = JSON.stringify(body);
            if (typeof serialized === 'string') {
                return { type: 'object', byteLength: Utils.toArray(serialized, 'utf8').length };
            }
        }
        catch {
            // Ignore JSON serialization issues for logging purposes only
        }
        return { type: typeof body, byteLength: 0 };
    }
    async waitForPendingCertificateRequests(peer) {
        const timeoutMs = 30000;
        const checkIntervalMs = 100;
        const startedAt = Date.now();
        while (peer.pendingCertificateRequests.length > 0) {
            if (Date.now() - startedAt > timeoutMs) {
                throw new Error('Timeout waiting for certificate request to complete');
            }
            await this.wait(checkIntervalMs);
        }
    }
    composePaymentLogDetails(url, context) {
        return {
            url,
            request: context.requestSummary,
            payment: {
                satoshis: context.satoshisRequired,
                transactionBase64: context.transactionBase64,
                derivationPrefix: context.derivationPrefix,
                derivationSuffix: context.derivationSuffix,
                serverIdentityKey: context.serverIdentityKey,
                clientIdentityKey: context.clientIdentityKey
            },
            attempts: {
                used: context.attempts,
                max: context.maxAttempts
            },
            errors: context.errors
        };
    }
    logPaymentAttempt(level, message, details) {
        const prefix = '[AuthFetch][Payment]';
        if (level === 'error') {
            console.error(`${prefix} ${message}`, details);
        }
        else if (level === 'warn') {
            console.warn(`${prefix} ${message}`, details);
        }
        else if (typeof console.info === 'function') {
            console.info(`${prefix} ${message}`, details);
        }
        else {
            console.log(`${prefix} ${message}`, details);
        }
    }
    createPaymentErrorEntry(attempt, error) {
        const entry = {
            attempt,
            timestamp: new Date().toISOString(),
            message: '',
            stack: undefined
        };
        if (error instanceof Error) {
            entry.message = error.message;
            entry.stack = error.stack ?? undefined;
        }
        else {
            entry.message = Utils.toSafeString(error);
        }
        return entry;
    }
    getPaymentRetryDelay(attempt) {
        const baseDelay = 250;
        const multiplier = Math.min(attempt, 5);
        return baseDelay * multiplier;
    }
    async wait(ms) {
        if (ms <= 0) {
            return;
        }
        await new Promise(resolve => setTimeout(resolve, ms));
    }
    buildPaymentFailureError(url, context, lastError) {
        const message = `Paid request to ${url} failed after ${context.attempts}/${context.maxAttempts} attempts. Sent ${context.satoshisRequired} satoshis to ${context.serverIdentityKey}.`;
        const error = new Error(message);
        const failureDetails = {
            request: context.requestSummary,
            payment: {
                satoshis: context.satoshisRequired,
                transactionBase64: context.transactionBase64,
                derivationPrefix: context.derivationPrefix,
                derivationSuffix: context.derivationSuffix,
                serverIdentityKey: context.serverIdentityKey,
                clientIdentityKey: context.clientIdentityKey
            },
            attempts: {
                used: context.attempts,
                max: context.maxAttempts
            },
            errors: context.errors
        };
        error.details = failureDetails;
        if (lastError instanceof Error) {
            ;
            error.cause = lastError;
        }
        return error;
    }
    async normalizeBodyToNumberArray(body) {
        // 0. Null / undefined
        if (body == null) {
            return [];
        }
        // 1. number[]
        if (Array.isArray(body) && body.every(item => typeof item === 'number')) {
            return body; // Return the array as is
        }
        // 2. string
        if (typeof body === 'string') {
            return Utils.toArray(body, 'utf8');
        }
        // 3. ArrayBuffer / TypedArrays
        if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
            const typedArray = body instanceof ArrayBuffer
                ? new Uint8Array(body)
                : new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
            return Array.from(typedArray);
        }
        // 4. Blob
        if (body instanceof Blob) {
            const arrayBuffer = await body.arrayBuffer();
            return Array.from(new Uint8Array(arrayBuffer));
        }
        // 5. FormData
        if (body instanceof FormData) {
            const entries = [];
            body.forEach((value, key) => {
                entries.push([key, typeof value === 'string' ? value : value.name]);
            });
            const urlEncoded = new URLSearchParams(entries).toString();
            return Utils.toArray(urlEncoded, 'utf8');
        }
        // 6. URLSearchParams
        if (body instanceof URLSearchParams) {
            return Utils.toArray(body.toString(), 'utf8');
        }
        // 7. ReadableStream
        if (body instanceof ReadableStream) {
            throw new TypeError('ReadableStream cannot be directly converted to number[].');
        }
        // 8. Plain object JSON body
        if (typeof body === 'object')
            return Utils.toArray(JSON.stringify(body), 'utf8');
        // 9. Fallback
        throw new Error('Unsupported body type in this SimplifiedFetch implementation.');
    }
}
//# sourceMappingURL=AuthFetch.js.map